import { useEffect, useRef } from 'react';
import { Calculation } from '@servicetitan/financing-api-client';
import { PreApprovalDto } from '@servicetitan/financing-api-client/src/prequals-api';
import { SetupDataStore } from '@servicetitan/financing-workflow-shared';
import {
    getGeneralDisclosureText,
    isGreenSkyProvider,
    isRenderableGreenSkyPreApproval,
} from '@servicetitan/financing-utils-shared';
import { FinancedAmountStore } from '../financing-information/financedAmountStore';
import { useLoanAmountValidation } from '../providers/useLoanAmountValidation';
import { usePreApprovalCalculation } from './hooks';
import { PreApprovalStore } from './preApprovalStore';

export type PreApprovalEligibility =
    | { kind: 'absent' }
    | { kind: 'loading'; preApproval?: PreApprovalDto }
    | {
          kind: 'in-range';
          preApproval: PreApprovalDto;
          calculation: Calculation;
          generalDisclosure: string;
      }
    | {
          kind: 'out-of-range';
          preApproval: PreApprovalDto;
          calculation?: Calculation;
          generalDisclosure: string;
      };

interface RetainedCalculation {
    key: string;
    calculation: Calculation;
}

const getCalculationKey = (
    preApproval: PreApprovalDto,
    provider: string | null | undefined,
    tenantId: string | null | undefined,
    buId: string | null | undefined,
    state: string | null | undefined,
) =>
    JSON.stringify({
        provider: provider ?? '',
        preApprovalId: preApproval.id ?? '',
        planNumber: preApproval.planNumber ?? '',
        tenantId: tenantId ?? '',
        buId: buId ?? '',
        state: state ?? '',
    });

/**
 * Resolves current GreenSky pre-approval eligibility without selecting page UI. Retained
 * calculations are display data only; current eligibility always uses the current request.
 */
export const usePreApprovalEligibility = (): PreApprovalEligibility => {
    const { preApproval, isLoading: isPreApprovalLoading } = PreApprovalStore.useStore();
    const { setupData } = SetupDataStore.useStore();
    const { financedAmount } = FinancedAmountStore.useStore();
    const { isValidLoanAmount } = useLoanAmountValidation(financedAmount);
    const retainedCalculation = useRef<RetainedCalculation | null>(null);

    const isGreenSky = isGreenSkyProvider(setupData?.provider);
    const hasRenderablePreApproval =
        preApproval != null &&
        isRenderableGreenSkyPreApproval(preApproval) &&
        !!preApproval.planNumber;
    const hasValidAmount = Number.isFinite(financedAmount) && financedAmount > 0;
    const isApplicationFlow = !setupData?.prequalificationId && !setupData?.loanId;
    const calculationEnabled =
        isGreenSky &&
        isApplicationFlow &&
        hasRenderablePreApproval &&
        hasValidAmount &&
        isValidLoanAmount;

    const calculationQuery = usePreApprovalCalculation({
        preApprovalId: preApproval?.id,
        planNumber: preApproval?.planNumber,
        amount: financedAmount,
        tenantId: setupData?.tenantId ?? null,
        buId: setupData?.buId ?? null,
        state: setupData?.state,
        enabled: calculationEnabled,
    });

    const calculationKey =
        preApproval == null
            ? null
            : getCalculationKey(
                  preApproval,
                  setupData?.provider,
                  setupData?.tenantId,
                  setupData?.buId,
                  setupData?.state,
              );
    const row =
        preApproval == null
            ? undefined
            : calculationQuery.data?.find(item => item.planNumber === preApproval.planNumber);
    const hasUsableRow =
        row != null &&
        row.minLoanAmount <= Math.min(preApproval?.amount ?? 0, row.maxLoanAmount || Infinity);

    useEffect(() => {
        if (calculationKey != null && row != null && hasUsableRow) {
            retainedCalculation.current = { key: calculationKey, calculation: row };
        }
    }, [calculationKey, hasUsableRow, row]);

    if (!isGreenSky || !isApplicationFlow) {
        return { kind: 'absent' };
    }

    if (preApproval == null) {
        return isPreApprovalLoading ? { kind: 'loading' } : { kind: 'absent' };
    }

    if (!hasRenderablePreApproval || !hasValidAmount) {
        return { kind: 'absent' };
    }

    const generalDisclosure = getGeneralDisclosureText(preApproval);
    const retained =
        retainedCalculation.current?.key === calculationKey
            ? retainedCalculation.current.calculation
            : undefined;

    if (!isValidLoanAmount) {
        return {
            kind: 'out-of-range',
            preApproval,
            calculation: retained,
            generalDisclosure,
        };
    }

    if (calculationQuery.isError || !hasUsableRow || row == null) {
        return calculationQuery.isLoading || calculationQuery.isFetching
            ? { kind: 'loading', preApproval }
            : { kind: 'absent' };
    }

    if (calculationQuery.isLoading || calculationQuery.isFetching) {
        return { kind: 'loading', preApproval };
    }

    if (row.eligibleForApplication && financedAmount <= preApproval.amount!) {
        return {
            kind: 'in-range',
            preApproval,
            calculation: row,
            generalDisclosure,
        };
    }

    return {
        kind: 'out-of-range',
        preApproval,
        calculation: row,
        generalDisclosure,
    };
};
